// line 543 staat het versienummer //
import React, { useState, useCallback, useEffect, useRef } from 'react';
import './App.css';
import config from './config';
import TimerDialog from './components/TimerDialog';
import EndDialog from './components/EndDialog';
import PlayerSettingsDialog from './components/PlayerSettingsDialog';
import DeviceSettingsDialog from './components/DeviceSettingsDialog';
import CompetitionDialog from './components/CompetitionDialog';
import ViewerConnectDialog from './components/ViewerConnectDialog';
import { competitionDb } from './utils/competitionDb';
import { getDeviceSettings, DeviceSettings } from './utils/device';
import { RefereeWebRtcManager, ScoreboardPayload, ConnectionStatus } from './utils/webRtcManager';
import Login from './components/Login';

interface Player {
  name: string;
  score: number;
  gamesWon: number;
  isServing: boolean;
  squashLevelsId?: string;
  club?: string;
  level?: number;
  location?: string;
  countryRanking?: string;
  countryScope?: string;
  worldRanking?: number;
  nlSquashGrade?: string | number;
  sbnId?: string;
  sbnProfile?: string;
  sbnPlayerLevel?: number | string;
  isCaptain?: boolean;
}

interface ServiceHistory {
  player: 1 | 2;
  box: 'left' | 'right';
  timestamp: number;
}

interface GameResult {
  winner: string;
  score: string;
  gameNumber: number;
  player1Score: number;
  player2Score: number;
  serviceBox?: 'left' | 'right';
  duration: number;
  scoreHistory: ScoreHistoryItem[];
}

interface ScoreHistoryItem {
  server: 1 | 2;
  serviceBox: 'left' | 'right';
  score1: number;
  score2: number;
  winner: 1 | 2 | null;
  letType?: string;
  letSide?: number;
  suffix?: string;
}

interface Point {
  player: 'player1' | 'player2';
  score: string;
}

interface AppState {
  matchStarted: boolean;
  matchStartTime: number | null;
  gameStartTime: number | null;
  serviceStartTime: number | null;
  matchTime: number;
  gameTime: number;
  serviceTime: number;
  gameTimes: string[];
  player1: Player;
  player2: Player;
  currentServer: 1 | 2;
  serviceHistory: ServiceHistory[];
  editingPlayer: 1 | 2 | null;
  serviceBox: 'left' | 'right';
  gameResults: GameResult[];
  matchWinner: string | null;
  showMobileFrame: boolean;
  isPortrait: boolean;
  currentDate: string;
  currentTime: string;
  scoreHistory: ScoreHistoryItem[];
  showConfirmation: boolean;
  showTimerPopup: boolean;
  timerDuration: number;
  timerTitle: string;
}

interface SquashLevelsPlayer {
  id: string;
  name: string;
  club: string;
  level: number;
  location: string;
  lastPlayed?: string;
}

// Add interfaces for search results
interface PlayerSearchResult {
  id: string;
  name: string;
  level?: number;
  club?: string;
  location?: string;
  rankings?: {
    world?: number;
    national?: number;
    regional?: number;
  };
  clubInfo?: {
    name?: string;
    location?: string;
  };
  grades?: {
    nlSquashGrade?: number;
    sbnNumber?: number;
  };
}

interface PlayerDetailsPopupProps {
  isOpen: boolean;
  player: PlayerSearchResult;
  onClose: () => void;
}


const PlayerDetailsPopup: React.FC<PlayerDetailsPopupProps> = ({ isOpen, player, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="player-details-popup-overlay">
      <div className="player-details-popup">
        <h3>Speler Details</h3>
        <div className="player-details-content">
          <div className="detail-row">
            <span className="detail-label">Naam:</span>
            <span className="detail-value">{player.name}</span>
          </div>

          {/* Rankings Section */}
          {player.rankings && (
            <div className="detail-section">
              <h4>Rankings</h4>
              {player.rankings.national && (
                <div className="detail-row">
                  <span className="detail-label">Nederland:</span>
                  <span className="detail-value">{player.rankings.national}</span>
                </div>
              )}
              {player.rankings.world && (
                <div className="detail-row">
                  <span className="detail-label">Wereld:</span>
                  <span className="detail-value">{player.rankings.world}</span>
                </div>
              )}
            </div>
          )}

          {/* Club Info Section */}
          {player.clubInfo && (
            <div className="detail-section">
              <h4>Club Informatie</h4>
              {player.clubInfo.name && (
                <div className="detail-row">
                  <span className="detail-label">Club:</span>
                  <span className="detail-value">{player.clubInfo.name}</span>
                </div>
              )}
              {player.clubInfo.location && (
                <div className="detail-row">
                  <span className="detail-label">Locatie:</span>
                  <span className="detail-value">{player.clubInfo.location}</span>
                </div>
              )}
            </div>
          )}

          {/* Grades Section */}
          {player.grades && (
            <div className="detail-section">
              <h4>Grades</h4>
              {player.grades.nlSquashGrade && (
                <div className="detail-row">
                  <span className="detail-label">NL Squash Grade:</span>
                  <span className="detail-value">{player.grades.nlSquashGrade}</span>
                </div>
              )}
              {player.grades.sbnNumber && (
                <div className="detail-row">
                  <span className="detail-label">SBN Nummer:</span>
                  <span className="detail-value">{player.grades.sbnNumber}</span>
                </div>
              )}
            </div>
          )}

          {/* Level Section */}
          {player.level && (
            <div className="detail-section">
              <h4>Level</h4>
              <div className="detail-row">
                <span className="detail-label">SquashLevels:</span>
                <span className="detail-value">{player.level}</span>
              </div>
            </div>
          )}
        </div>
        <button className="close-button" onClick={onClose}>Sluiten</button>
      </div>
    </div>
  );
};

const generateColorMatrix = () => {
  // 14 main colors (distinct, as in your palette)
  const mainColors = [
    '#FF0000', // Red
    '#FF8000', // Orange
    '#FFFF00', // Yellow
    '#00FF00', // Green
    '#00FFFF', // Cyan
    '#0000FF', // Blue
    '#8000FF', // Purple
    '#FF00FF', // Magenta
    '#964B00', // Brown
    '#FFC0CB', // Pink
    '#808080', // Gray
    '#000000', // Black
    '#FFFFFF', // White
    '#008000', // Dark Green
  ];

  // Helper to lighten a hex color
  function lighten(hex: string, percent: number) {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.round(r + (255 - r) * percent);
    g = Math.round(g + (255 - g) * percent);
    b = Math.round(b + (255 - b) * percent);
    return `rgb(${r},${b},${b})`;
  }

  const colors: string[] = [];
  for (let row = 0; row < 2; row++) {
    for (let col = 0; col < 7; col++) {
      const colorIndex = row * 7 + col;
      if (colorIndex < mainColors.length) {
        colors.push(mainColors[colorIndex]);
      }
    }
  }
  return colors;
};


// Add debounce function at the top level
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const VERSION = process.env.REACT_APP_VERSION || 'v26.10.31';
const IS_STANDALONE = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone || false;

// Cookie Helpers
const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
};

const getCookie = (name: string) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const removeCookie = (name: string) => {
  document.cookie = name + '=; Max-Age=-99999999; path=/;';
};

// Simple JWT Decode (base64)
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const App: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [token, setToken] = useState<string | null>(() => {
    // Prioriteit: 1. LocalStorage 'jwt_token', 2. LocalStorage 'token', 3. Cookie 'squash_token'
    return localStorage.getItem('jwt_token') || localStorage.getItem('token') || getCookie('squash_token');
  });
  const [pouleInfo, setPouleInfo] = useState<any>(null);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [user, setUser] = useState<any>(() => {
    // Prioriteit: 1. LocalStorage, 2. Cookie
    const saved = localStorage.getItem('user') || getCookie('squash_user');
    if (!saved) return null;
    try { return JSON.parse(saved); } catch (e) {
      console.warn('[AUTH] Corrupt user data in storage, clearing');
      localStorage.removeItem('user');
      return null;
    }
  });
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [syncFailCount, setSyncFailCount] = useState(0);

  // Session Verification & Expiry Check
  useEffect(() => {
    const verifySession = async () => {
      if (!token) return;

      // 1. Calculate days remaining from JWT locally
      const decoded = decodeJWT(token);
      if (decoded && decoded.exp) {
        // Standard JWT 'exp' is in seconds, but we verify both to be safe
        const expMs = decoded.exp < 10000000000 ? decoded.exp * 1000 : decoded.exp;
        const nowMs = Date.now();
        const diffMs = expMs - nowMs;
        const remaining = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
        
        console.log(`[AUTH] License expiring in ${remaining} days (exp: ${new Date(expMs).toISOString()})`);
        setDaysRemaining(remaining);
        
        if (remaining <= 0 && diffMs < 0) {
          console.warn('Token expired');
          performLocalLogout();
          return;
        }
      }

      // 2. Verify with backend
      try {
        const response = await fetch(`${config.apiUrl}/api/v1/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          console.warn('Session verification failed on server');
          performLocalLogout();
        } else {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            localStorage.setItem('user', JSON.stringify(data.user));
            if (data.user.email) {
              localStorage.setItem('remembered_email', data.user.email.toLowerCase().trim());
            }
            setCookie('squash_user', JSON.stringify(data.user), 365);
          }
        }
      } catch (err) {
        console.error('Network error during session verification');
      }
    };

    verifySession();
  }, [token]);

  const [matchStarted, setMatchStarted] = useState(false);
  // ... (rest of initializations)
  const [matchStartTime, setMatchStartTime] = useState<number | null>(null);
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);
  const [matchTime, setMatchTime] = useState(0);
  const [gameTime, setGameTime] = useState(0);
  const [player1, setPlayer1] = useState<Player>({
    name: 'Player 1', score: 0, gamesWon: 0, isServing: false, squashLevelsId: '', club: '', level: undefined
  });
  const [player2, setPlayer2] = useState<Player>({
    name: 'Player 2', score: 0, gamesWon: 0, isServing: false, squashLevelsId: '', club: '', level: undefined
  });
  const [currentServer, setCurrentServer] = useState<1 | 2>(1);
  const [serviceBox, setServiceBox] = useState<'left' | 'right'>('right');
  const [gameResults, setGameResults] = useState<GameResult[]>([]);
  const [matchWinner, setMatchWinner] = useState<string | null>(null);
  const [scoreHistory, setScoreHistory] = useState<ScoreHistoryItem[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPlayerCustomization, setShowPlayerCustomization] = useState(false);
  const [showTimerPopup, setShowTimerPopup] = useState(false);
  const [tempPlayer1Color, setTempPlayer1Color] = useState('#8000FF');
  const [tempPlayer2Color, setTempPlayer2Color] = useState('#FF8000');
  const [currentDate, setCurrentDate] = useState(() => new Date().toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }));
  const [currentTime, setCurrentTime] = useState(() => new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }));
  const historyScrollRef = useRef<HTMLDivElement>(null);
  const lastScoreRef = useRef<HTMLDivElement>(null);
  const [showTimerDialog, setShowTimerDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<1 | 2 | null>(null);
  const [deviceSettings, setDeviceSettingsState] = useState<DeviceSettings>(() => getDeviceSettings());

  // ─── Toeschouwersscherm WebRTC P2P Koppeling ───────────────────────────────
  const [showViewerDialog, setShowViewerDialog] = useState(false);
  const [viewerSessionId, setViewerSessionId] = useState<string>(() => localStorage.getItem('squash_viewer_pair_id') || '');
  const [viewerStatus, setViewerStatus] = useState<ConnectionStatus>('disconnected');
  const [viewerLogs, setViewerLogs] = useState<string[]>([]);
  const refereeRtcRef = useRef<RefereeWebRtcManager | null>(null);
  // Stabiele ref zodat handleConnectViewer broadcastScoreToViewer kan aanroepen
  // zonder een circulaire useCallback-dependency te maken.
  const broadcastScoreRef = useRef<() => void>(() => undefined);

  const addViewerLog = useCallback((msg: string) => {
    setViewerLogs(prev => [...prev.slice(-8), msg]);
  }, []);

  const handleConnectViewer = useCallback((pin: string) => {
    if (refereeRtcRef.current) {
      refereeRtcRef.current.destroy();
    }
    setViewerSessionId(pin);
    localStorage.setItem('squash_viewer_pair_id', pin);

    const rtc = new RefereeWebRtcManager(
      pin,
      (status) => setViewerStatus(status),
      (msg) => addViewerLog(msg),
      () => broadcastScoreRef.current()
    );
    refereeRtcRef.current = rtc;
    rtc.init(config.wsUrl);
  }, [addViewerLog]);

  const handleDisconnectViewer = useCallback(() => {
    if (refereeRtcRef.current) {
      refereeRtcRef.current.destroy();
      refereeRtcRef.current = null;
    }
    setViewerStatus('disconnected');
    localStorage.removeItem('squash_viewer_pair_id');
    setViewerSessionId('');
    addViewerLog('Verbinding verbroken door gebruiker');
  }, [addViewerLog]);

  // Broadcast score direct over lokaal P2P data-kanaal bij elke scoremutatie
  const broadcastScoreToViewer = useCallback(() => {
    if (refereeRtcRef.current && refereeRtcRef.current.isConnected()) {
      const payload: ScoreboardPayload = {
        type: 'SCORE_UPDATE',
        player1: {
          name: player1.name,
          score: player1.score,
          gamesWon: player1.gamesWon,
          isServing: currentServer === 1,
          pastGames: gameResults.map(g => g.player1Score)
        },
        player2: {
          name: player2.name,
          score: player2.score,
          gamesWon: player2.gamesWon,
          isServing: currentServer === 2,
          pastGames: gameResults.map(g => g.player2Score)
        },
        server: currentServer,
        serviceBox: serviceBox,
        gameNumber: gameResults.length + 1,
        matchStarted: matchStarted,
        timestamp: Date.now()
      };
      refereeRtcRef.current.sendScore(payload);
    }
  }, [player1, player2, currentServer, serviceBox, gameResults, matchStarted]);

  // Houd broadcastScoreRef altijd up-to-date met de laatste versie van de callback
  useEffect(() => {
    broadcastScoreRef.current = broadcastScoreToViewer;
  });

  useEffect(() => {
    broadcastScoreToViewer();
  }, [broadcastScoreToViewer, viewerStatus]);

  // Verwerk ?pair=XXXX URL parameter van QR-code scan
  useEffect(() => {
    // Controleer of er een ?pair=... parameter in de URL staat (van QR-code scan)
    const urlParams = new URLSearchParams(window.location.search);
    const pairParam = urlParams.get('pair');
    if (pairParam && /^\d{4}$/.test(pairParam)) {
      console.log('[Viewer] QR-scan gedetecteerd, koppelcode:', pairParam);
      // Verwijder de parameter uit de URL (zonder pagina te herladen)
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      // Open de viewer-dialoog en start verbinding automatisch
      setShowViewerDialog(true);
      handleConnectViewer(pairParam);
    }

    return () => {
      if (refereeRtcRef.current) {
        refereeRtcRef.current.destroy();
      }
    };
  }, []);

  // Zelf-herstellende opslag: als er één ontbreekt, vul de andere aan
  useEffect(() => {
    if (token && user) {
      const storedToken = localStorage.getItem('token');
      const cookieToken = getCookie('squash_token');
      const storedUser = localStorage.getItem('user');
      const cookieUser = getCookie('squash_user');

      if (!storedToken || !storedUser) {
        console.log('[AUTH] Herstellen naar LocalStorage...');
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
      }
      if (!cookieToken || !cookieUser) {
        console.log('[AUTH] Herstellen naar Cookies...');
        setCookie('squash_token', token, 365);
        setCookie('squash_user', JSON.stringify(user), 365);
      }

      // Sync user info to deviceSettings if missing
      if (user.name && !deviceSettings.refereeName) {
        console.log('[AUTH] Syncing user name to device settings...');
        setDeviceSettingsState(prev => {
          const updated = {
            ...prev,
            refereeName: user.name,
            refereePlayerId: user.playerId,
            email: user.email
          };
          localStorage.setItem('squash_device_settings', JSON.stringify(updated));
          return updated;
        });
      }
    }
  }, [token, user, deviceSettings.refereeName]);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [showCompetitionDialog, setShowCompetitionDialog] = useState(false);
  const [activePouleId, setActivePouleId] = useState<number | null>(() => {
    const saved = getCookie('squash_user') || localStorage.getItem('user');
    if (!saved) return null;
    try { const u = JSON.parse(saved); return u?.poule_id ? Number(u.poule_id) : null; } catch (e) { return null; /* corrupt storage */ }
  });
  const [activeTeamId, setActiveTeamId] = useState<number | null>(() => {
    const saved = getCookie('squash_user') || localStorage.getItem('user');
    if (!saved) return null;
    try { const u = JSON.parse(saved); return u?.team_id ? Number(u.team_id) : null; } catch (e) { return null; /* corrupt storage */ }
  });

  useEffect(() => {
    if (user) {
      if (user.poule_id) setActivePouleId(Number(user.poule_id));
      if (user.team_id) setActiveTeamId(Number(user.team_id));
    }
  }, [user]);
  const [syncStatus, setSyncStatus] = useState<'none' | 'syncing' | 'synced' | 'error'>('none');
  const [pendingUpdatesCount, setPendingUpdatesCount] = useState<number>(0);
  const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);
  const [lastCheckDate, setLastCheckDate] = useState<string | null>(null);

  const handleLoginSuccess = (user: any, token: string) => {
    setUser(user);
    setToken(token);
    // Persistent Storage (jwt_token + legacy token + remembered_email)
    localStorage.setItem('jwt_token', token);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    if (user?.email) {
      localStorage.setItem('remembered_email', user.email.toLowerCase().trim());
    }
    setCookie('squash_user', JSON.stringify(user), 365);
    setCookie('squash_token', token, 365);
    
    setDeviceSettingsState(prev => ({
      ...prev,
      refereeName: user.name,
      refereePlayerId: user.playerId,
      email: user.email
    }));
  };

  const performLocalLogout = () => {
    setUser(null);
    setToken(null);
    // Verwijder sessie-tokens (LET OP: remembered_email mag NOOIT verlopen/verwijderd worden)
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    removeCookie('squash_user');
    removeCookie('squash_token');
    setSyncStatus('none');
    setActivePouleId(null);
    competitionDb.clearAll().catch(err => console.error('[AUTH] Failed to clear DB', err));
    setShowDeviceSettings(false);
  };

  const handleProfileRemoval = async () => {
    if (!window.confirm('Weet je zeker dat je jouw profiel wilt verwijderen van dit apparaat? Je moet dan opnieuw registreren.')) {
      return;
    }

    try {
      // Call backend to reset verification
      if (token) {
        await fetch(`${config.apiUrl}/api/v1/auth/reset-profile`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (err) {
      console.warn('[AUTH] Kon server-side reset niet voltooien, maar we gaan door met lokale verwijdering.');
    }

    performLocalLogout();
  };

  // --- VERSION UPDATE LOGIC ---
  useEffect(() => {
    const handleUpdateAvailable = (event: any) => {
      console.log('[UPDATE] New version detected via Service Worker');
      setSwRegistration(event.detail);
      
      if (!IS_STANDALONE) {
        // In browser: automatically refresh if no match is active
        // If match is active, we show the button as fallback
        const appState = document.body.getAttribute('data-match-started');
        if (appState !== 'true') {
          console.log('[UPDATE] Auto-refreshing browser for new version...');
          window.location.reload();
        } else {
          setNewVersionAvailable(true);
        }
      } else {
        // Installed app: always show update button
        setNewVersionAvailable(true);
      }
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);
    return () => window.removeEventListener('sw-update-available', handleUpdateAvailable);
  }, []);

  const handleApplyUpdate = () => {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  };

  const performSync = useCallback(async (isManual = false) => {
    if (!token || !user) return;
    
    try {
      if (!isManual) setSyncStatus('syncing');
      
      // Update check time immediately
      const now = new Date().toISOString();
      await competitionDb.setLastCheck(now);
      setLastCheckDate(now);

      // 1. Check local status
      const status = await competitionDb.getSyncStatus();
      let targetPouleId = status.pouleId;

      // 2. If no poule selected yet, try to auto-detect context from user name
      if (!targetPouleId && user.name) {
        const contextRes = await fetch(`${config.apiUrl}/api/v1/auth/context`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: user.name })
        });
        const contextData = await contextRes.json();
        if (contextData.success && contextData.context?.poule_id) {
          targetPouleId = contextData.context.poule_id;
          const targetTeamId = contextData.context.team_id;
          setActiveTeamId(targetTeamId);
          setActivePouleId(targetPouleId);

          setUser((prevUser: any) => {
            const updatedUser = {
              ...prevUser,
              team_id: targetTeamId,
              team_name: contextData.context.team_name || prevUser?.team_name,
              poule_id: targetPouleId,
              poule_name: contextData.context.poule_name || prevUser?.poule_name,
              competition_name: contextData.context.competition_name || prevUser?.competition_name,
              club_name: contextData.context.club_name || prevUser?.club_name
            };
            try {
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } catch (storageErr) {
              console.warn('[SYNC] Kon user niet lokaal opslaan', storageErr);
            }
            return updatedUser;
          });

          console.log(`[SYNC] Autodetected context for ${user.name}: Poule ${targetPouleId}, Team ${targetTeamId}`);
        } else {
          console.log(`[SYNC] No context found for ${user.name}`, contextData);
        }
      }

      if (!targetPouleId) {
        console.log('[SYNC] No targetPouleId, sync skipped.');
        setSyncStatus('none');
        return;
      }

      // Update local state if different from DB
      setActivePouleId(prev => prev !== targetPouleId ? targetPouleId : prev);

      // 3. Check version on server (Poule-specific!)
      const versionRes = await fetch(`${config.apiUrl}/api/v1/competition/version/${targetPouleId}`);
      if (!versionRes.ok) throw new Error(`Version check failed: ${versionRes.status}`);
      const versionData = await versionRes.json();
      
      if (versionData.success) {
        const serverVersion = String(versionData.version);
        const localVersion = String(status.version);

        // Als er een versie-verschil is of force/isManual is aangevraagd:
        if (serverVersion !== localVersion || isManual) {
          const syncUrl = `${config.apiUrl}/api/v1/competition/sync/${targetPouleId}`;
          const syncRes = await fetch(syncUrl);
          const syncData = await syncRes.json();

          const hasLocalData = Boolean(status.pouleId && status.version);

          if (syncData.success && syncData.data) {
            if (isManual || !hasLocalData) {
              // Direct lokaal opslaan bij handmatige actie OF als er nog geen lokale data in IndexedDB staat!
              console.log(`[SYNC] ${isManual ? 'Handmatige/directe' : 'Initiële'} update voor poule ${targetPouleId}. Opslaan in lokale database...`);
              await competitionDb.saveSyncData(syncData.data);
              setPendingUpdatesCount(0);
              setSyncStatus('synced');
              setLastSyncDate(new Date().toISOString());
              setSyncFailCount(0);
            } else {
              // Achtergrondcontrole bij bestaande data: bereken aantal wijzigingen voor het rode bolletje op de competitiebutton
              const diffCount = await competitionDb.getPendingUpdatesCount(syncData.data);
              console.log(`[SYNC] Achtergrondcheck: ${diffCount} pending update(s) gevonden voor poule ${targetPouleId}`);
              setPendingUpdatesCount(diffCount);
              setSyncStatus('synced');
            }
          } else {
            console.error('[SYNC] Server returned error:', syncData.message);
            setSyncStatus('error');
            // Alleen wissen als het ECHT niet gevonden is (404-achtig)
            if (syncData.message?.toLowerCase().includes('niet gevonden')) {
              console.warn('[SYNC] Poule niet meer geldig. Wissen...');
              await competitionDb.clearAll();
              setActivePouleId(null);
            }
          }
        } else {
          setPendingUpdatesCount(0);
          setSyncStatus('synced');
          setLastSyncDate(status.lastSync);
          setSyncFailCount(0);
        }
      } else {
        setSyncStatus('error');
      }
    } catch (error) {
      console.error('[SYNC ERROR]', error);
      setSyncStatus('error');
      setSyncFailCount(prev => prev + 1);
    }
  }, [token, user?.name, user?.id]); // Removed activeTeamId to break loop

  // --- COMPETITION SYNC LOGIC (Phase 3.2) ---
  useEffect(() => {
    performSync();
  }, [performSync]);

  // Timers
  useEffect(() => {
    const timer = setInterval(() => {
      if (!matchWinner) {
        if (matchStarted) {
          setGameTime(prev => prev + 1);
          setMatchTime(prev => prev + 1);
        }
      }
      setCurrentDate(new Date().toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }));
      setCurrentTime(new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }));
    }, 1000);
    return () => clearInterval(timer);
  }, [matchWinner, matchStarted]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const undoLastPoint = useCallback(() => {
    setScoreHistory(prev => {
      if (prev.length > 1) {
        // Haal het nieuwe laatste item op (na undo)
        const newHistory = prev.slice(0, -1);
        const last = newHistory[newHistory.length - 1];
        // Zet de scores terug naar de waarden van het laatste history-item
        setPlayer1(p => ({ ...p, score: last.score1 }));
        setPlayer2(p => ({ ...p, score: last.score2 }));
        // Verwijder de suffix van het nieuwe laatste item
        newHistory[newHistory.length - 1] = { ...last, suffix: '' };
        return newHistory;
      }
      return prev; // Laat altijd minimaal 1 item staan
    });
  }, []);

  // Helper: maak een nieuw history item
  function makeHistoryItem(
    server: 1 | 2,
    serviceBox: 'left' | 'right',
    score1: number,
    score2: number,
    winner: 1 | 2 | null,
    suffix = ''
  ): ScoreHistoryItem {
    return { server, serviceBox, score1, score2, winner, suffix };
  }

  // START knop
  const handleStart = () => {
    setMatchStarted(true);
    setMatchStartTime(Date.now());
    setGameTime(0);
    setMatchTime(0);
    setCurrentServer(1);
    setServiceBox('right');
    // Start history: server 1, right, 0-0, geen score suffix
    setScoreHistory([makeHistoryItem(1, 'right', 0, 0, null, '')]);
  };

  // L/R button vóór eerste punt of tijdens service beurt
  const handleServiceBoxSwitch = () => {
    setServiceBox(prev => {
      const newBox = prev === 'left' ? 'right' : 'left';
      setScoreHistory(prevHist => {
        if (prevHist.length === 0) {
          // Nog geen history, maak eerste item
          return [makeHistoryItem(currentServer, newBox, 0, 0, null, '')];
        } else {
          // Update laatste regel (serviceBox)
          return prevHist.map((item, idx) =>
            idx === prevHist.length - 1 ? { ...item, serviceBox: newBox } : item
          );
        }
      });
      return newBox;
    });
  };

  // Server button vóór eerste punt of tijdens service beurt
  const handleServerSwitch = () => {
    setCurrentServer(prev => {
      const newServer = prev === 1 ? 2 : 1;
      setScoreHistory(prevHist => {
        if (prevHist.length === 0) {
          // Nog geen history, maak eerste item
          return [makeHistoryItem(newServer, 'right', 0, 0, null, '')];
        } else {
          // Update laatste regel (server)
          return prevHist.map((item, idx) =>
            idx === prevHist.length - 1 ? { ...item, server: newServer } : item
          );
        }
      });
      setServiceBox('right');
      return newServer;
    });
  };

  // PUNT TOEGEKEND
  const handleScore = useCallback((player: 1 | 2) => {
    if (matchWinner) return;
    if (!matchStarted) {
      setMatchStarted(true);
      setMatchStartTime(Date.now());
    }
    const currentPlayer = player === 1 ? player1 : player2;
    const otherPlayer = player === 1 ? player2 : player1;
    const setCurrentPlayer = player === 1 ? setPlayer1 : setPlayer2;
    const setOtherPlayer = player === 1 ? setPlayer2 : setPlayer1;

    // Bepaal huidige score vóór punt
    const prevScore1 = player1.score;
    const prevScore2 = player2.score;
    const prevServer = currentServer;
    const prevBox = serviceBox;

    // Bepaal of server het punt wint
    const serverWins = player === currentServer;
    // Score na punt
    const newScore1 = prevScore1 + (player === 1 ? 1 : 0);
    const newScore2 = prevScore2 + (player === 2 ? 1 : 0);

    // Update score
    setCurrentPlayer(prev => ({ ...prev, score: prev.score + 1 }));

    setScoreHistory(prevHist => {
      const newHist = [...prevHist];
      // 1. Werk laatste regel bij: voeg score of '-' toe
      if (newHist.length > 0) {
        newHist[newHist.length - 1] = {
          ...newHist[newHist.length - 1],
          score1: prevScore1,
          score2: prevScore2,
          winner: prevServer,
          suffix: serverWins ? String(prevServer === 1 ? newScore1 : newScore2) : '-'
        };
      }
      // 2. Voeg nieuwe regel toe voor volgende service beurt, maar NIET als het game einde is
      const isGameWin = (currentPlayer.score + 1 >= 11 && currentPlayer.score + 1 - otherPlayer.score >= 2);
      if (!isGameWin) {
        if (serverWins) {
          // Zelfde server, andere zijde
          newHist.push(makeHistoryItem(prevServer, prevBox === 'left' ? 'right' : 'left', newScore1, newScore2, null, ''));
        } else {
          // Andere server, altijd vanaf rechts
          newHist.push(makeHistoryItem(player, 'right', newScore1, newScore2, null, ''));
        }
      }
      return newHist;
    });

    // Update service voor volgende rally
    if (serverWins) {
      setServiceBox(prev => prev === 'left' ? 'right' : 'left');
    } else {
      setCurrentServer(player);
      setServiceBox('right');
    }

    // Check for game win
    if (currentPlayer.score + 1 >= 11 && currentPlayer.score + 1 - otherPlayer.score >= 2) {
      const newGamesWon = currentPlayer.gamesWon + 1;
      // 1. Add the final rally to the history only if not already added
      let updatedHistory: ScoreHistoryItem[] = [];
      setScoreHistory(prevHist => {
        const newHist = [...prevHist];
        // Only update if the last entry does not already reflect the final score
        const last = newHist[newHist.length - 1];
        const lastScore = (prevServer === 1 ? newScore1 : newScore2).toString();
        if (!last || last.suffix !== lastScore) {
          if (newHist.length > 0) {
            newHist[newHist.length - 1] = {
              ...newHist[newHist.length - 1],
              score1: prevScore1,
              score2: prevScore2,
              winner: prevServer,
              suffix: serverWins ? lastScore : '-'
            };
          }
        }
        updatedHistory = newHist;
        return newHist;
      });
      setTimeout(() => {
        setGameResults(prev => [...prev, {
          winner: currentPlayer.name || `Speler ${player}`,
          score: `${currentPlayer.score + 1}-${otherPlayer.score}`,
          gameNumber: gameResults.length + 1,
          player1Score: player === 1 ? currentPlayer.score + 1 : otherPlayer.score,
          player2Score: player === 1 ? otherPlayer.score : currentPlayer.score + 1,
          serviceBox: serviceBox,
          duration: gameTime,
          scoreHistory: updatedHistory
        }]);
      }, 0);
      setCurrentPlayer(prev => ({ ...prev, gamesWon: newGamesWon, score: 0 }));
      setOtherPlayer(prev => ({ ...prev, score: 0 }));
      setGameTime(0);
      setScoreHistory([makeHistoryItem(player, 'right', 0, 0, null, '')]);
      setServiceBox('right');
      setCurrentServer(player);
      if (newGamesWon >= 3) {
        setMatchWinner(currentPlayer.name || `Speler ${player}`);
      }
      return;
    }
  }, [player1, player2, matchWinner, matchStarted, gameTime, serviceBox, gameResults, currentServer, scoreHistory]);

  const handleEndGame = () => {
    setShowConfirmation(true);
  };

  const confirmEndGame = () => {
    setMatchStarted(false);
    setMatchStartTime(null);
    setGameTime(0);
    setPlayer1({
      name: 'Player 1',
      score: 0,
      gamesWon: 0,
      isServing: false,
      squashLevelsId: '',
      club: '',
      level: undefined
    });
    setPlayer2({
      name: 'Player 2',
      score: 0,
      gamesWon: 0,
      isServing: false,
      squashLevelsId: '',
      club: '',
      level: undefined
    });
    setCurrentServer(1);
    setServiceBox('right');
    setGameResults([]);
    setMatchWinner(null);
    setScoreHistory([]);
    setShowConfirmation(false);
    setShowPlayerCustomization(false);
  };

  const cancelEndGame = () => {
    setShowConfirmation(false);
  };

  // Helper voor games-winst-balk
  const renderGamesBar = () => (
    <div className="games-bar">
      <span className="games-bar-player1">{player1.gamesWon}</span>
      <span className="games-bar-dash">-</span>
      <span className="games-bar-player2">{player2.gamesWon}</span>
    </div>
  );

  // Helper voor gamegeschiedenis (uitslagen per game)
  const renderGameHistory = () => (
    <div className="game-history-center">
      <div className="game-history-list">
        {gameResults.map((game, idx) => (
          <div key={idx} className="game-history-item">{game.player1Score} {game.player2Score}</div>
        ))}
      </div>
    </div>
  );

  // Helper voor puntenverloop per game (kolommen)
  const renderGameColumns = () => (
    <div className="game-columns">
      {gameResults.map((game, idx) => (
        <div key={idx} className={`game-column game-column-${idx + 1}`}> {/* kleur per game */}
          <div className="game-column-header">{idx + 1}</div>
          {game.scoreHistory.map((item, i) => (
            <div key={i} className="game-column-point">{item.score1}-{item.score2}</div>
          ))}
        </div>
      ))}
    </div>
  );

  // Remove the renderHistoryContainer function
  const renderHistoryContainer = () => null;

  // Add event listeners to prevent refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (matchStarted) {
        e.preventDefault();
        e.returnValue = '';
        handleEndGame();
        return '';
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent F5 and Ctrl+R
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        e.preventDefault();
        handleEndGame();
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [matchStarted]); // Only re-run if matchStarted changes

  // Add this function before the return statement
  const getSbnLevelDisplay = (level?: number | string | null) => {
    if (level !== undefined && level !== null && String(level).trim() !== '' && String(level).trim() !== 'null' && String(level).trim() !== 'undefined') {
      return String(level).trim();
    }
    return '-';
  };

  const getBallStatus = () => {
    // MATCH BALL: een speler heeft 2 games, 10 of meer punten, en minimaal 1 punt voorsprong
    if (
      (player1.gamesWon === 2 && player1.score >= 10 && player1.score - player2.score >= 1) ||
      (player2.gamesWon === 2 && player2.score >= 10 && player2.score - player1.score >= 1)
    ) {
      return 'MATCH BALL';
    }
    // GAME BALL: een speler heeft 10 of meer punten, en minimaal 1 punt voorsprong
    if (
      (player1.score >= 10 && player1.score - player2.score >= 1) ||
      (player2.score >= 10 && player2.score - player1.score >= 1)
    ) {
      return 'GAME BALL';
    }
    return null;
  };

  useEffect(() => {
    document.body.setAttribute('data-match-started', matchStarted ? 'true' : 'false');
  }, [matchStarted]);

  if (!token) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="App">
      {/* Info-header bovenaan */}
      <div className="info-header">
        <div className="version-status">
          {newVersionAvailable && (
            <button className="update-btn-header" onClick={handleApplyUpdate}>
              UPDATE
            </button>
          )}
          <span className="app-version">{VERSION}</span>
          {daysRemaining !== null && (
            <>
              <span className="status-separator"> - </span>
              <span 
                className={`glow-check ${syncStatus}`} 
                style={{ 
                  color: syncStatus === 'synced' ? '#4CAF50' : syncStatus === 'error' ? '#FF9800' : '#888' 
                }}
                title={
                  syncStatus === 'synced' ? `Sync ok: ${lastSyncDate}` : 
                  syncStatus === 'error' ? 'Geen verbinding (offline)' : 
                  'Syncing...'
                }
              >✓</span>
              {syncFailCount > 0 && (
                <span className="sync-fail-count" style={{ color: '#FF4D4D', fontWeight: 'bold', marginLeft: '4px', fontSize: '0.8rem' }}>
                  {syncFailCount}
                </span>
              )}
              <span className="days-left">{daysRemaining}</span>
            </>
          )}
        </div>
        <button 
          className={`viewer-header-btn ${viewerStatus}`}
          onClick={() => setShowViewerDialog(true)}
          title={viewerStatus === 'connected' ? 'Toeschouwersscherm Verbonden' : 'Koppel Toeschouwersscherm'}
        >
          📺 {viewerStatus === 'connected' ? 'LIVE' : 'VIEWER'}
        </button>
      </div>

      {/* Action buttons container */}
      <div className="action-buttons-container">
        <div className="action-buttons-row">
          <button 
            onClick={handleStart} 
            disabled={matchStarted}
            className={(!matchStarted && (player1.name !== 'Player 1' || player2.name !== 'Player 2')) ? 'start-active' : ''}
          >START</button>
          <button onClick={handleServiceBoxSwitch}>L/R</button>
          <button className="server-button" onClick={handleServerSwitch}></button>
          <button onClick={undoLastPoint}>UNDO</button>
          <button onClick={handleEndGame}>END</button>
        </div>
        <div className="action-buttons-row">
          <button
            className="player-button btn-competition"
            onClick={async () => {
              await performSync(true);
              setShowCompetitionDialog(true);
            }}
          >
            COMPETITIE
            {pendingUpdatesCount > 0 && (
              <span className="competition-update-badge">{pendingUpdatesCount}</span>
            )}
          </button>
          <button
            className="timer-button"
            onClick={() => setShowTimerDialog(true)}
          >TIMER</button>
          <button
            className="player-button btn-settings"
            onClick={() => setShowDeviceSettings(true)}
            title="Instellingen"
            aria-label="Instellingen"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Spectator container met huidige scores */}
      <div className="spectator-container" data-scoring-active={matchStarted ? 'true' : 'false'}>
        {/* Add the ball indicator here, before the header row */}
        {getBallStatus() && (
          <div className={`ball-indicator ${getBallStatus() === 'GAME BALL' ? 'game-ball' : 'match-ball'}`}>
            {getBallStatus()}
          </div>
        )}

        <div className="spectator-header-row">
          {/* Speler 1 linksboven */}
          <div className="player-column">
            <div className="player-name-row" onClick={() => { setEditingPlayer(1); setShowPlayerCustomization(true); }}>
              <span className="sbn-level-badge sbn-level-badge--left">
                {getSbnLevelDisplay(player1.sbnPlayerLevel)}
              </span>
              <span className="player-name">{player1.name.split(' ').slice(1).join(' ')}</span>
            </div>
            <div className="player-name" onClick={() => { setEditingPlayer(1); setShowPlayerCustomization(true); }}>{player1.name.split(' ')[0]}</div>
            <div className="player-club">{player1.club || '---'}</div>
            <div className="player-details-table">
              <div className="row">
                <span className="label">NLD:</span>
                <span className="value">
                  {player1.countryRanking !== undefined && player1.countryRanking !== null && String(player1.countryRanking).trim() !== ''
                    ? player1.countryRanking
                    : '---'}
                </span>
              </div>
              <div className="row">
                <span className="label">LEVEL:</span>
                <span className="value">
                  {player1.level !== undefined && player1.level !== null && String(player1.level).trim() !== ''
                    ? player1.level
                    : '---'}
                </span>
              </div>
              <div className="row">
                <span className="label">GRADE:</span>
                <span className="value">
                  {player1.nlSquashGrade !== undefined && player1.nlSquashGrade !== null && String(player1.nlSquashGrade).trim() !== ''
                    ? player1.nlSquashGrade
                    : '---'}
                </span>
              </div>
              <div className="row">
                <span className="label">WORLD:</span>
                <span className="value">
                  {player1.worldRanking !== undefined && player1.worldRanking !== null && String(player1.worldRanking).trim() !== ''
                    ? player1.worldRanking
                    : '---'}
                </span>
              </div>
            </div>
          </div>

          {/* Vertical Game History Bar in the middle */}
          <div className="game-history-column">
            <div className="games-bar">
              <span className="games-bar-player1">{player1.gamesWon}</span>
              <span className="games-bar-dash">-</span>
              <span className="games-bar-player2">{player2.gamesWon}</span>
            </div>
            {/* Totale match tijd direct onder de games-bar, als de wedstrijd is afgelopen */}
            {matchWinner && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '25px 0 0 0',
                fontFamily: 'monospace',
                fontWeight: 'bold',
                fontSize: '1em',
                color: '#FFFFFF',
                position: 'relative',
                border: 'none',
                background: 'none',
                padding: 0
              }}>
                <span style={{
                  display: 'inline-block',
                  border: '1px solid #000',
                  borderRadius: 0,
                  padding: '0 2px',
                  lineHeight: 1,
                  verticalAlign: 'middle',
                  background: '#FFF',
                  color: '#000',
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  fontSize: '1em'
                }}>
                  {(() => {
                    const totalMin = Math.floor(matchTime / 60).toString().padStart(2, '0');
                    const totalSec = (matchTime % 60).toString().padStart(2, '0');
                    return (
                      <>
                        <span style={{ lineHeight: 1 }}>{totalMin}</span>
                        <span style={{ lineHeight: 1 }}>:</span>
                        <span style={{ lineHeight: 1 }}>{totalSec}</span>
                      </>
                    );
                  })()}
                </span>
              </div>
            )}
            <div className="game-history-bar">
              {gameResults.map((game, index) => (
                <div key={index} className="game-score-entry">
                  <span className="score-left">{game.player1Score}</span>
                  <span className="score-right">{game.player2Score}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Speler 2 rechtsboven */}
          <div className="player-column right">
            <div className="player-name-row player-name-row--right" onClick={() => { setEditingPlayer(2); setShowPlayerCustomization(true); }}>
              <span className="player-name">{player2.name.split(' ').slice(1).join(' ')}</span>
              <span className="sbn-level-badge sbn-level-badge--right">
                {getSbnLevelDisplay(player2.sbnPlayerLevel)}
              </span>
            </div>
            <div className="player-name" onClick={() => { setEditingPlayer(2); setShowPlayerCustomization(true); }}>{player2.name.split(' ')[0]}</div>
            <div className="player-club">{player2.club || '---'}</div>
            <div className="player-details-table">
              <div className="row">
                <span className="label">NLD:</span>
                <span className="value">
                  {player2.countryRanking !== undefined && player2.countryRanking !== null && String(player2.countryRanking).trim() !== ''
                    ? player2.countryRanking
                    : '---'}
                </span>
              </div>
              <div className="row">
                <span className="label">LEVEL:</span>
                <span className="value">
                  {player2.level !== undefined && player2.level !== null && String(player2.level).trim() !== ''
                    ? player2.level
                    : '---'}
                </span>
              </div>
              <div className="row">
                <span className="label">GRADE:</span>
                <span className="value">
                  {player2.nlSquashGrade !== undefined && player2.nlSquashGrade !== null && String(player2.nlSquashGrade).trim() !== ''
                    ? player2.nlSquashGrade
                    : '---'}
                </span>
              </div>
              <div className="row">
                <span className="label">WORLD:</span>
                <span className="value">
                  {player2.worldRanking !== undefined && player2.worldRanking !== null && String(player2.worldRanking).trim() !== ''
                    ? player2.worldRanking
                    : '---'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* New scores container at the bottom */}
        <div className={`spectator-scores-container${matchStarted ? ' scoring-active' : ''}`}>
          <div
            className={`spectator-score player1${!matchStarted ? ' disabled' : ''}`}
            style={{ '--player1-color': tempPlayer1Color } as React.CSSProperties}
            onClick={() => { if (matchStarted) handleScore(1); }}
          >
            {player1.score}
            {currentServer === 1 && (
              <div
                className={`spectator-service-dot ${serviceBox}`}
                style={{ '--player-color': tempPlayer1Color } as React.CSSProperties}
              />
            )}
          </div>
          <div
            className={`spectator-score player2${!matchStarted ? ' disabled' : ''}`}
            style={{ '--player2-color': tempPlayer2Color } as React.CSSProperties}
            onClick={() => { if (matchStarted) handleScore(2); }}
          >
            {player2.score}
            {currentServer === 2 && (
              <div
                className={`spectator-service-dot ${serviceBox}`}
                style={{ '--player-color': tempPlayer2Color } as React.CSSProperties}
              />
            )}
          </div>
        </div>
      </div>

      {/* History container with 5 games */}
      <div className="history-container">
        <div className="history-bars">
          {[1, 2, 3, 4, 5].map(num => {
            // Toon de huidige game (scoreHistory) in de juiste kolom
            const isCurrentGame = num === gameResults.length + 1;
            const history = isCurrentGame ? scoreHistory : (gameResults[num - 1]?.scoreHistory || []);
            // Check: als de wedstrijd voorbij is, geen L/R meer tonen in nieuwe kolom
            if (matchWinner && isCurrentGame) {
              return (
                <div key={num} className="history-axis" data-game-number={num}>
                  <div className="history-score-container" />
                </div>
              );
            }
            // Bepaal of er een gespeelde game is en de duur moet worden getoond
            const game = gameResults[num - 1];
            // Bepaal de achtergrondkleur op basis van de winnaar
            let winnerColor: string | undefined = undefined;
            if (game) {
              if (game.player1Score > game.player2Score) {
                winnerColor = tempPlayer1Color;
              } else if (game.player2Score > game.player1Score) {
                winnerColor = tempPlayer2Color;
              }
            }
            return (
              <div
                key={num}
                className="history-axis"
                data-game-number={num}
                style={winnerColor ? {
                  backgroundColor: winnerColor,
                  transition: 'background 0.3s',
                  '--winner-color': winnerColor
                } as React.CSSProperties : {}}
              >
                <div className="history-score-container">
                  {history.map((item, idx) => {
                    const sideText = `${item.serviceBox[0].toUpperCase()}${item.suffix ?? ''}`;
                    // Bepaal of de server de rally verloor (suffix === '-')
                    const serverLost = item.suffix === '-';
                    // Score van de tegenstander na deze rally
                    let opponentScore = '';
                    if (serverLost) {
                      if (item.server === 1) {
                        opponentScore = (item.score2 + 1).toString();
                      } else if (item.server === 2) {
                        opponentScore = (item.score1 + 1).toString();
                      }
                    }
                    // Bepaal blokjeskleur: alleen voor gespeelde games (dus als game bestaat)
                    let blockBg: string | undefined = undefined;
                    if (game) {
                      if (game.player1Score > game.player2Score) {
                        blockBg = tempPlayer1Color;
                      } else if (game.player2Score > game.player1Score) {
                        blockBg = tempPlayer2Color;
                      }
                    }
                    return (
                      <div key={idx} className="history-score-entry" style={{ position: 'relative', height: '20px', display: 'flex', alignItems: 'center', borderRadius: 2 }}>
                        {/* Links van de as voor speler 1 */}
                        {item.server === 1 && (
                          <span className="history-score-left" style={{ position: 'absolute', right: '50%', transform: 'translateX(-6px)', color: '#fff', backgroundColor: blockBg ? blockBg : undefined, borderRadius: 2, fontWeight: 'bold', fontFamily: 'monospace', minWidth: '24px', textAlign: 'right' }}>{sideText}</span>
                        )}
                        {item.server === 2 && serverLost && (
                          <span className="history-score-left" style={{ position: 'absolute', right: '50%', transform: 'translateX(-6px)', color: '#fff', backgroundColor: blockBg ? blockBg : undefined, borderRadius: 2, fontWeight: 'bold', fontFamily: 'monospace', minWidth: '24px', textAlign: 'right' }}>{opponentScore}</span>
                        )}
                        {/* As */}
                        <span className="mark-line" style={{ position: 'absolute', left: '50%', width: '1px', height: '100%', backgroundColor: 'rgba(255,255,255,0.15)' }}></span>
                        {/* Rechts van de as voor speler 2 */}
                        {item.server === 2 && (
                          <span className="history-score-right" style={{ position: 'absolute', left: '50%', transform: 'translateX(6px)', color: '#fff', backgroundColor: blockBg ? blockBg : undefined, borderRadius: 2, fontWeight: 'bold', fontFamily: 'monospace', minWidth: '24px', textAlign: 'left' }}>{sideText}</span>
                        )}
                        {item.server === 1 && serverLost && (
                          <span className="history-score-right" style={{ position: 'absolute', left: '50%', transform: 'translateX(6px)', color: '#fff', backgroundColor: blockBg ? blockBg : undefined, borderRadius: 2, fontWeight: 'bold', fontFamily: 'monospace', minWidth: '24px', textAlign: 'left' }}>{opponentScore}</span>
                        )}
                      </div>
                    );
                  })}
                  {/* Voeg de duur toe onder de laatste score van een gespeelde game */}
                  {game && game.duration > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: 4,
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      fontSize: '1em',
                      color: '#FFFFFF',
                      position: 'relative',
                      height: 20,
                      border: 'none',
                      background: 'none',
                      padding: 0
                    }}>
                      {/* MM:SS met gecentreerde dubbele punt, border direct om de tekst */}
                      <span style={{
                        display: 'inline-block',
                        border: '1px solid #000',
                        borderRadius: 0,
                        padding: '0 2px',
                        lineHeight: 1,
                        verticalAlign: 'middle',
                        background: '#FFF',
                        color: '#000',
                        fontFamily: 'monospace',
                        fontWeight: 'bold',
                        fontSize: '1em'
                      }}>
                        {(() => {
                          const min = Math.floor(game.duration / 60).toString().padStart(2, '0');
                          const sec = (game.duration % 60).toString().padStart(2, '0');
                          return (
                            <>
                              <span style={{ lineHeight: 1 }}>{min}</span>
                              <span style={{ lineHeight: 1 }}>:</span>
                              <span style={{ lineHeight: 1 }}>{sec}</span>
                            </>
                          );
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Replace the old confirmation dialog with the new EndDialog */}
      <EndDialog
        isOpen={showConfirmation}
        onConfirm={confirmEndGame}
        onCancel={cancelEndGame}
      />

      {showPlayerCustomization && editingPlayer && (
        <PlayerSettingsDialog
          isOpen={showPlayerCustomization}
          onClose={() => { setShowPlayerCustomization(false); setEditingPlayer(null); }}
          onSave={(player1Name, player2Name, player1Color, player2Color, player1Details, player2Details) => {
            setTempPlayer1Color(player1Color);
            setTempPlayer2Color(player2Color);
            setShowPlayerCustomization(false);
            setEditingPlayer(null);
            // Hier worden de spelers bijgewerkt via de state van de backup zelf
            setPlayer1(prev => ({ ...prev, name: player1Name, ...player1Details }));
            setPlayer2(prev => ({ ...prev, name: player2Name, ...player2Details }));
          }}
          initialPlayer1Name={player1.name}
          initialPlayer2Name={player2.name}
          initialPlayer1Color={tempPlayer1Color}
          initialPlayer2Color={tempPlayer2Color}
          /* VERWIJDER DEZE TWEE REGELS: */
          /* setPlayer1={setPlayer1} */
          /* setPlayer2={setPlayer2} */
          /* VOEG DEZE TOE (indien nodig voor de nieuwe component): */
          player1Details={player1}
          player2Details={player2}
          targetPlayer={editingPlayer}
        />
      )}

      {showCompetitionDialog && (
        <CompetitionDialog
          isOpen={showCompetitionDialog}
          syncStatus={syncStatus}
          activeTeamId={activeTeamId}
          onClose={() => setShowCompetitionDialog(false)}
          onSelectPlayers={(p1, p2) => {
            setPlayer1(prev => ({ ...prev, ...p1, score: 0, gamesWon: 0 }));
            setPlayer2(prev => ({ ...prev, ...p2, score: 0, gamesWon: 0 }));
            setShowCompetitionDialog(false);
          }}
        />
      )}

      {showTimerDialog && (
        <TimerDialog
          isOpen={showTimerDialog}
          onClose={() => setShowTimerDialog(false)}
        />
      )}

      <ViewerConnectDialog
        isOpen={showViewerDialog}
        onClose={() => setShowViewerDialog(false)}
        status={viewerStatus}
        currentSessionId={viewerSessionId}
        logs={viewerLogs}
        onConnect={handleConnectViewer}
        onDisconnect={handleDisconnectViewer}
      />

      {showDeviceSettings && (
        <DeviceSettingsDialog
          isOpen={showDeviceSettings}
          onClose={() => setShowDeviceSettings(false)}
          user={user}
          onLogout={handleProfileRemoval}
          daysRemaining={daysRemaining}
          syncFailCount={syncFailCount}
          onCompetitionSaved={(newPouleId, newTeamId) => {
            setActivePouleId(newPouleId);
            if (newTeamId) {
              setActiveTeamId(newTeamId);
            }
            performSync(true);
          }}
        />
      )}
    </div>
  );
};

export default App;